'use client';

import { useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';

export default function AuthBootstrap({ children }: { children: React.ReactNode }) {
    const {
        hydration,
        needsServerLogout,
        clearNeedsServerLogout,
        resetAuth,
        setUserAuth,
        setUserAvatarUrl,
        setUserMedal,
        setUserBadges,
        setUserScore,
        setUserRegistrationStatus,
        setUserSpots,
    } = useAuthStore();

    const initializedRef = useRef(false);

    useEffect(() => {
        if (hydration !== 'hydrated') return;
        if (initializedRef.current) return;
        initializedRef.current = true;

        (async () => {
            try {
                if (needsServerLogout) {
                    await apiFetch('/auth/logout', { method: 'POST' });
                    clearNeedsServerLogout();
                }

                const response = await apiFetch('/users/me');
                const u = response?.user;
                if (!u?.schoomyers_id) {
                    resetAuth();
                    await apiFetch('/auth/logout', { method: 'POST' });
                    return;
                }

                setUserAuth({
                    schoomyersId: u.schoomyers_id,
                    nickname: u.nickname,
                    email: u.email,
                    selfIntroduction: u.self_introduction,
                });

                setUserAvatarUrl(u.avatar_resource_url);

                const m = u.signature_medal;
                setUserMedal({
                    medalCd: m.medal_cd,
                    achievementScore: m.achievement_score,
                    userSignatureMedalUrl: m.medal_resource_url,
                    medalName: m.medal_name,
                });

                setUserBadges(
                    u.signature_badges.map((b: any) => ({
                        badgeCd: b.badge_cd,
                        achievementScore: b.achievement_score,
                        awardDateTime: b.award_date_time,
                    })),
                );

                const s = u.score;
                setUserScore({
                    achievementScore: s.achievement_score,
                    totalMedalScore: s.total_medal_score,
                    totalBadgeScore: s.total_badge_score,
                    gearPostCount: s.gear_post_count,
                    gearLikeCount: s.gear_like_count,
                    festaEntryCheckinCount: s.festa_entry_checkin_count,
                    totalScore: s.total_score,
                    ownedLabCount: s.owned_lab_count,
                    editorLaunchCount: s.editor_launch_count,
                    whiteTeacherAnswerCount: s.white_teacher_answer_count,
                    editorWriteCount: s.editor_write_count,
                });

                const r = u.registration_status;
                setUserRegistrationStatus({
                    profile: r.profile,
                    address: r.address,
                    email: r.email,
                    sub_email: r.sub_email,
                    phone_number: r.phone_number,
                    password: r.password,
                    payment: r.payment,
                    service: r.service,
                    family: r.family,
                });

                setUserSpots(
                    (u.spots ?? []).map((s: any) => ({
                        hubId: s.hub_id,
                        organizationName: s.organization_name,
                        branchName: s.branch_name,
                    })),
                );
            } catch {
                resetAuth();
                try {
                    await apiFetch('/auth/logout', { method: 'POST' });
                } catch {
                    void 0;
                }
            }
        })();
    }, [hydration]);

    return <>{children}</>;
}
